import { useEffect, useState, type ReactNode } from 'react';
import './vitrine.css';
import { C } from '../theme';
import {
  Micro,
  Telecharger,
  Cloche,
  CalendrierCoche,
  Coche,
  Panier,
  Famille,
  Horloge,
  Enveloppe,
} from '../icones';
import { naviguer, CHEMIN_CONFIDENTIALITE, CHEMIN_DEMO } from '../routeur';
import { EnTete, Lien, Pied, TelephoneApercu } from './composants';

// L'evenement d'installation n'est pas dans les types standard du DOM : il est propre a
// Chromium. On le decrit ici plutot que de le forcer en `any`.
interface EvenementInstallation extends Event {
  prompt: () => Promise<void>;
}

function dejaInstallee(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari iOS n'implemente pas display-mode et expose ce drapeau non standard.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function Installer() {
  const [invite, setInvite] = useState<EvenementInstallation | null>(null);
  const [montrerEtapes, setMontrerEtapes] = useState(false);
  const [installee, setInstallee] = useState(dejaInstallee);

  useEffect(() => {
    const surInvite = (e: Event) => {
      e.preventDefault();
      setInvite(e as EvenementInstallation);
    };
    // Chrome emet `appinstalled` apres une installation reussie : sans cet ecouteur, la
    // page continuerait de proposer d'installer ce qui vient de l'etre.
    const surInstallation = () => {
      setInstallee(true);
      setInvite(null);
    };
    window.addEventListener('beforeinstallprompt', surInvite);
    window.addEventListener('appinstalled', surInstallation);
    return () => {
      window.removeEventListener('beforeinstallprompt', surInvite);
      window.removeEventListener('appinstalled', surInstallation);
    };
  }, []);

  const installer = () => {
    if (installee) {
      naviguer('/app');
      return;
    }
    if (invite !== null) {
      void invite.prompt();
      return;
    }
    // Aucun mecanisme automatique : c'est le cas de Safari sur iPhone, et de Chrome tant
    // que ses criteres ne sont pas remplis. On montre alors la marche a suivre plutot
    // qu'un bouton qui ne ferait rien.
    setMontrerEtapes(true);
  };

  return (
    <div className="v-page">
      <EnTete
        actionPrincipale={
          <Lien vers="/app" className="v-bouton v-bouton--secondaire v-bouton--compact">
            Ouvrir Colette
          </Lien>
        }
      />

      <main>
        {/* --- Hero --- */}
        <section className="v-section">
          <div className="v-contenu v-hero">
            <div className="v-hero__texte">
              <p className="v-surtitre">Assistant familial</p>
              <h1 className="v-h1">
                Tu vis ta vie.
                <br />
                Colette fait le tri.
              </h1>
              <p className="v-lead">
                Dicte ou écris ce que tu as en tête. Colette en tire tes rendez-vous, tes tâches
                et tes rappels — et ne fait jamais rien sans ton accord.
              </p>

              <div className="v-boutons" style={{ marginTop: 'var(--e2)' }}>
                <button type="button" className="v-bouton v-bouton--principal" onClick={installer}>
                  {installee ? null : <Telecharger taille={19} couleur="#FFFFFF" epaisseur={2.1} />}
                  {installee ? 'Ouvrir Colette' : "Installer l'application"}
                </button>
                <Lien vers={CHEMIN_DEMO} className="v-bouton v-bouton--secondaire">
                  Voir comment ça marche
                </Lien>
              </div>

              <p className="v-hero__note">
                Gratuit · s'installe depuis le navigateur, sans magasin d'applications
              </p>

              {montrerEtapes ? <MarcheASuivre /> : null}
            </div>

            <TelephoneApercu />
          </div>
        </section>

        {/* --- Comment ca marche --- */}
        <section className="v-section v-section--carte">
          <div className="v-contenu">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--e3)', maxWidth: '46ch' }}>
              <p className="v-surtitre">En trois temps</p>
              <h2 className="v-h2">Une phrase suffit</h2>
            </div>

            <div className="v-grille v-grille--3" style={{ marginTop: 'var(--e6)' }}>
              <Etape
                numero={1}
                titre="Tu lui parles"
                texte="Une phrase, dictée ou écrite, au moment où tu y penses."
                exemple="« Jeudi je vais à Lyon pour une réunion à 10h, il me faut le dossier et le chargeur. »"
              />
              <Etape
                numero={2}
                titre="Elle comprend"
                texte="Colette en tire un rendez-vous, un départ à prévoir, une tâche et la liste de ce qu'il faut emporter."
              />
              <Etape
                numero={3}
                titre="Tu valides"
                texte="Elle te montre ce qu'elle a compris. Tu gardes, tu corriges, ou tu refuses. Rien ne bouge avant."
              />
            </div>
          </div>
        </section>

        {/* --- Ce qu'elle gere --- */}
        <section className="v-section">
          <div className="v-contenu">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--e3)', maxWidth: '46ch' }}>
              <p className="v-surtitre">Toute la vie courante</p>
              <h2 className="v-h2">Un seul endroit, plus de charge mentale</h2>
            </div>

            <div className="v-grille v-grille--3" style={{ marginTop: 'var(--e6)' }}>
              <Atout
                fond={C.violetFond}
                icone={<Horloge taille={21} couleur={C.violetTexte} epaisseur={1.9} />}
                titre="Rendez-vous et planning"
                texte="Réunions, médecin, école : chaque chose à sa date, à la bonne heure, où que tu sois."
              />
              <Atout
                fond={C.corailFond}
                icone={<Cloche taille={21} couleur={C.corailTexte} epaisseur={1.9} />}
                titre="Rappels au bon moment"
                texte="Préparer le sac la veille, signer l'autorisation avant ce soir. Prévenue quand c'est utile, pas avant."
              />
              <Atout
                fond={C.mentheFond}
                icone={<Panier taille={21} couleur={C.mentheTexte} epaisseur={1.9} />}
                titre="Listes et courses"
                texte="Le repas de vendredi devient une liste d'ingrédients. Rien ne s'oublie entre le frigo et le magasin."
              />
              <Atout
                fond={C.violetFond}
                icone={<Famille taille={21} couleur={C.violetTexte} epaisseur={1.9} />}
                titre="Toute la famille"
                texte="Les activités des enfants, les sorties scolaires, ce que chacun doit emporter."
              />
              <Atout
                fond={C.corailFond}
                icone={<CalendrierCoche taille={21} couleur={C.corailTexte} epaisseur={1.9} />}
                titre="Papiers et échéances"
                texte="Carte d'identité, passeport, carte grise, assurance : prévenue avant l'expiration, pas après."
              />
              <Atout
                fond={C.mentheFond}
                icone={<Enveloppe taille={21} couleur={C.mentheTexte} epaisseur={1.9} />}
                titre="Ce qui arrive par mail"
                texte="Transfère-lui le message de l'école : elle en fait un rendez-vous et une liste."
              />
            </div>
          </div>
        </section>

        {/* --- Confiance --- */}
        <section className="v-section v-section--serree">
          <div className="v-contenu">
            <div className="v-confiance">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--e3)' }}>
                <p className="v-surtitre">Le principe</p>
                <h2 className="v-h2">Rien sans ton accord</h2>
                <p className="v-texte">
                  Colette propose, tu décides. C'est la règle qui a présidé à toute sa
                  conception, et elle ne souffre aucune exception.
                </p>
              </div>

              <ul className="v-liste">
                <Garantie>
                  Rien n'entre dans ton planning avant que tu aies validé, proposition par
                  proposition.
                </Garantie>
                <Garantie>
                  Tu vois toujours <strong>ce qu'elle a compris</strong> de ta phrase, avant qu'elle
                  agisse.
                </Garantie>
                <Garantie>
                  Tu peux corriger ou refuser n'importe quelle proposition, sans justification.
                </Garantie>
                <Garantie>
                  Tes données restent dans ton foyer. Elles ne sont ni vendues, ni utilisées pour
                  entraîner une intelligence artificielle.{' '}
                  <Lien vers={CHEMIN_CONFIDENTIALITE}>En savoir plus</Lien>
                </Garantie>
              </ul>
            </div>
          </div>
        </section>

        {/* --- Appel final --- */}
        <section className="v-section v-section--serree">
          <div
            className="v-contenu"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--e4)',
              textAlign: 'center',
            }}
          >
            <h2 className="v-h2">Essaie-la ce soir</h2>
            <p className="v-lead" style={{ textAlign: 'center' }}>
              Une phrase dictée en rentrant, et ta semaine se range toute seule.
            </p>
            <div className="v-boutons" style={{ justifyContent: 'center' }}>
              <button type="button" className="v-bouton v-bouton--principal" onClick={installer}>
                {installee ? null : <Telecharger taille={19} couleur="#FFFFFF" epaisseur={2.1} />}
                {installee ? 'Ouvrir Colette' : "Installer l'application"}
              </button>
            </div>
          </div>
        </section>
      </main>

      <Pied />
    </div>
  );
}

function Etape({
  numero,
  titre,
  texte,
  exemple,
}: {
  numero: number;
  titre: string;
  texte: string;
  exemple?: string;
}) {
  return (
    <div className="v-carte">
      <span className="v-etape__numero">{numero}</span>
      <h3 className="v-h3">{titre}</h3>
      <p className="v-texte">{texte}</p>
      {exemple !== undefined ? (
        <p
          style={{
            margin: 0,
            fontSize: '0.875rem',
            fontStyle: 'italic',
            lineHeight: 1.55,
            color: C.violetTexte,
            background: C.violetFond,
            borderRadius: 12,
            padding: 'var(--e3) var(--e4)',
          }}
        >
          {exemple}
        </p>
      ) : null}
    </div>
  );
}

function Atout({
  fond,
  icone,
  titre,
  texte,
}: {
  fond: string;
  icone: ReactNode;
  titre: string;
  texte: string;
}) {
  return (
    <div className="v-carte">
      <span className="v-carte__icone" style={{ background: fond }}>
        {icone}
      </span>
      <h3 className="v-h3">{titre}</h3>
      <p className="v-texte">{texte}</p>
    </div>
  );
}

function Garantie({ children }: { children: ReactNode }) {
  return (
    <li>
      <span style={{ flex: 'none', marginTop: 2 }}>
        <Coche taille={18} couleur={C.menthe} epaisseur={2.6} />
      </span>
      <span>{children}</span>
    </li>
  );
}

function MarcheASuivre() {
  return (
    <div
      className="v-carte"
      style={{ marginTop: 'var(--e3)', gap: 'var(--e3)', borderColor: C.violetBordure }}
    >
      <h3 className="v-h3">Pour installer Colette</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--e2)' }}>
        <p className="v-texte">
          <strong style={{ color: C.violetTexte }}>Android (Chrome)</strong> — menu ⋮ en haut à
          droite, puis « Ajouter à l'écran d'accueil ».
        </p>
        <p className="v-texte">
          <strong style={{ color: C.violetTexte }}>iPhone (Safari)</strong> — bouton Partager, puis
          « Sur l'écran d'accueil ».
        </p>
      </div>
      <p style={{ margin: 0, fontSize: '0.8125rem', color: C.tertiaire }}>
        L'icône Colette apparaît alors comme une application normale.
      </p>
    </div>
  );
}
